import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAddons, getMenu, restrucreMenu } from '../../services/menuService';
// import { getMenu, getAddons, restrucreMenu } from '../services/menuService';

const PRESIGNED_URL_API = 'https://us-central1-digislidesapp.cloudfunctions.net/s3GeneratePresignedUrl';
const S3_GET_ASSETS_API = 'https://us-central1-digislidesapp.cloudfunctions.net/s3GetAssets';
const S3_GET_ASSETS_JSON_API = 'https://us-central1-digislidesapp.cloudfunctions.net/s3GetAssetsJson';
const S3_GET_TEMPLATE_JSON_API = 'https://us-central1-digislidesapp.cloudfunctions.net/s3GetTemplateJson';

/**
 * Extract S3 key from a full S3 URL
 * Example: https://digisolutions-assets.s3.amazonaws.com/slideconfig/assets/pizza/pepperoni_pizza.png
 * Returns: slideconfig/assets/pizza/pepperoni_pizza.png
 */
const extractS3Key = (url) => {
  try {
    const urlObj = new URL(url);
    // Remove leading slash from pathname
    return urlObj.pathname.substring(1);
  } catch (error) {
    console.error('Error extracting S3 key from URL:', url, error);
    return null;
  }
};

/**
 * Generate presigned URL using Cloud Function
 * @param {string} s3Key - The S3 object key (path)
 * @returns {Promise<string>} - The presigned URL
 */
const generatePresignedUrl = async (s3Key, operationType = 'read') => {
  try {
    const response = await axios.post(
      PRESIGNED_URL_API,
      {
        s3Key: s3Key,
        folderPath: s3Key, // Support both for backward compatibility
        operationType: operationType,
        expiresIn: 3600, // 1 hour default
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Handle different response formats
    if (typeof response.data === 'string') {
      return response.data;
    }
    return response.data?.url || response.data?.presignedUrl || response.data?.data || response.data;
  } catch (error) {
    console.error('Error generating presigned URL for key:', s3Key, error);
    console.error('Error details:', error.response?.data || error.message);
    // Fallback to original URL if presigned URL generation fails
    return `https://digisolutions-assets.s3.amazonaws.com/${s3Key}`;
  }
};

// Async thunks for fetching assets
export const fetchBackgroundTemplates = createAsyncThunk(
  'assets/fetchBackgroundTemplates',
  async (templateJsonPath = 'slideconfig/config/menutemplates.json', { rejectWithValue }) => {
    try {
      // Step 1: Fetch template.json file from S3 via Cloud Function (avoids CORS issues)
      const templateData = await getDataFromS3('slideconfig/config/menutemplates.json', 'read');

      if (!templateData) {
        console.warn('template.json is empty or invalid');
        return [];
      }

      // Step 3: Transform the template data structure
      // New structure: Array of cuisines, each with a slides array
      const templates = [];

      if (Array.isArray(templateData)) {
        templateData.forEach((cuisine) => {
          if (cuisine.slides && Array.isArray(cuisine.slides)) {
            cuisine.slides.forEach((slide) => {
              // Determine template type based on isVideo flag
              const templateType = slide.isVideo ? 'video' : 'image';

              templates.push({
                id: `cuisine-${cuisine.name}-slide-${slide.templateId}`,
                type: templateType,
                templateId: slide.templateId,
                url: slide.finished,
                thumbnail: slide.blank || slide.finished,
                name: `${cuisine.cuisine_name} - Template ${slide.templateId}`,
                finished: slide.finished,
                blank: slide.blank,
                isCustom: slide.isCustom || false,
                isVideo: slide.isVideo || false,
              });
            });
          }
        });
      }

      return templates;
    } catch (error) {
      console.error('Error fetching background templates from template.json:', error);
      console.error('Error details:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch background templates');
    }
  }
);

export const getDataFromS3 = async (s3Key, operationType = 'read') => {
  const API_BASE = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-digislidesapp.cloudfunctions.net';
  try {
    // 1) Get presigned URL for READ
    const { data: presign } = await axios.post(
      `${API_BASE}/s3GeneratePresignedUrl`,
      {
        s3Key,
        operationType: operationType,
        expiresIn: 900,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const url = presign.presignedUrl || presign.url;
    if (!url) {
      throw new Error('No presigned URL returned for reading s3Key');
    }

    // 2) Read JSON directly from S3
    const resp = await axios.get(url, {
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (resp.status !== 200 || !resp.data) {
      return [];
    }

    const data = resp.data;
    return Array.isArray(data) ? data : [];
  } catch (error) {
    // If file doesn't exist or any other error, treat as empty config
    console.warn('readSlideConfig failed (using empty array):', error.message);
    return [];
  }
};

export const fetchAssets = createAsyncThunk(
  'assets/fetchAssets',
  async (assetsJsonPath = 'slideconfig/config/assets.json', { rejectWithValue }) => {
    try {

      const cuisines = await getDataFromS3('slideconfig/config/assets.json', 'read');

      if (!Array.isArray(cuisines)) {
        console.warn('assets.json does not contain an array of cuisines');
        return [];
      }

      // Step 3: Extract all images from all cuisines and collect S3 keys
      const imageData = [];
      cuisines.forEach((cuisine) => {
        if (cuisine.images && Array.isArray(cuisine.images)) {
          cuisine.images.forEach((image) => {
            if (image.uri) {
              const s3Key = extractS3Key(image.uri);
              if (s3Key) {
                imageData.push({
                  cuisine,
                  image,
                  s3Key,
                  originalUri: image.uri,
                });
              }
            }
          });
        }
      });


      // Step 5: Transform to match expected format
      const assets = imageData.map(({ cuisine, image, s3Key, originalUri, presignedUrl }) => ({
        id: `cuisine-${cuisine.id}-image-${image.id}`,
        type: 'image',
        url: originalUri,
        thumbnail: originalUri,
        name: `${cuisine.cuisine_name} - Image ${image.id}`,
        key: s3Key,
        originalUri: originalUri, // Keep original URI as fallback
        cuisineId: cuisine.id,
        cuisineName: cuisine.cuisine_name,
        cuisineImage: cuisine.cuisine_image,
        category: cuisine.cuisine_name.toLowerCase().replace(/\s+/g, '_'), // Use cuisine name as category
      }));

      return assets;
    } catch (error) {
      console.error('Error fetching assets from assets.json:', error);
      console.error('Error details:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch assets');
    }
  }
);

export const fetchMenuItems = createAsyncThunk(
  'assets/fetchMenuItems',
  async (params, { rejectWithValue }) => {
    try {
      const { store_id, api_token, syncMenu = 1, menuVersion = 'v1' } = params || {};

      if (!store_id || !api_token) {
        return rejectWithValue('store_id and api_token are required');
      }

      // Fetch menu and addons in parallel
      const [menu, addons] = await Promise.all([
        getMenu({ store_id, api_token, syncMenu }),
        getAddons({ store_id, api_token })
      ]);

      // Restructure menu into hierarchical format
      const restructuredMenu = await restrucreMenu(menu, addons, menuVersion);

      return restructuredMenu;
    } catch (error) {
      console.error('Error fetching menu items:', error);
      return rejectWithValue(error.message || 'Failed to fetch menu items');
    }
  }
);

const initialState = {
  // Background Templates
  backgroundTemplates: [],
  backgroundTemplatesLoading: false,
  backgroundTemplatesError: null,

  // Assets (images, videos, etc.)
  assets: [],
  assetsLoading: false,
  assetsError: null,

  // Menu Items (hierarchical structure: categories -> subcategories -> items)
  menuItems: [],
  menuItemsLoading: false,
  menuItemsError: null,

  // Filters
  selectedCategory: 'all',
  searchQuery: '',
};

const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    resetAssets: () => initialState,
  },
  extraReducers: (builder) => {
    // Background Templates
    builder
      .addCase(fetchBackgroundTemplates.pending, (state) => {
        state.backgroundTemplatesLoading = true;
        state.backgroundTemplatesError = null;
      })
      .addCase(fetchBackgroundTemplates.fulfilled, (state, action) => {
        state.backgroundTemplatesLoading = false;
        state.backgroundTemplates = action.payload;
      })
      .addCase(fetchBackgroundTemplates.rejected, (state, action) => {
        state.backgroundTemplatesLoading = false;
        state.backgroundTemplatesError = action.payload;
      });

    // Assets
    builder
      .addCase(fetchAssets.pending, (state) => {
        state.assetsLoading = true;
        state.assetsError = null;
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.assetsLoading = false;
        state.assets = action.payload;
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.assetsLoading = false;
        state.assetsError = action.payload;
      });

    // Menu Items
    builder
      .addCase(fetchMenuItems.pending, (state) => {
        state.menuItemsLoading = true;
        state.menuItemsError = null;
      })
      .addCase(fetchMenuItems.fulfilled, (state, action) => {
        state.menuItemsLoading = false;
        state.menuItems = action.payload;
      })
      .addCase(fetchMenuItems.rejected, (state, action) => {
        state.menuItemsLoading = false;
        state.menuItemsError = action.payload;
      });
  },
});

export const {
  setSelectedCategory,
  setSearchQuery,
  resetAssets,
} = assetsSlice.actions;

export default assetsSlice.reducer;

